import {
  Button,
  Flex,
  Paper,
  Loader,
  NumberInput,
  Group,
  Switch,
  Text,
  Select,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { initialize, updateField } from "../slices/form/emailTemplateScheduler";
import { JSX, useEffect, useState } from "react";
import {
  EmailReminder,
  recipientFilterTypeToText,
  RelativeTo,
  textToRecipientFilterType,
  TimeUnitOfMeasure,
} from "../slices/types";
import { DayWeekControl } from "../shared/components/DayWeekControl";
import { RelativeToControl } from "../shared/components/RelativeToControl";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { EbTooltip } from "../shared/components/EbTooltip";
import { IconDelete } from "../shared/components/Icons";
import {
  EmailGuestsRequest,
  useEmailGuestsMutation,
} from "../slices/api/emailApi";
import {
  displayInfoMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";

interface Props {
  emailTemplateId: string;
  disabled?: boolean;
  emailRemindersRelativeTo: RelativeTo;
  emailReminders: EmailReminder[];
  onChange: (
    emailRemindersRelativeTo: RelativeTo,
    emailReminders: EmailReminder[],
  ) => void;
}
const EmailTemplateScheduler = (props: Props) => {
  const { data, hasChanges } = useAppSelector(
    (state: RootState) => state.emailTemplateScheduler,
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      initialize({
        emailRemindersRelativeTo: props.emailRemindersRelativeTo ?? "ventStart",
        emailReminders: props.emailReminders,
      }),
    );
  }, [props.emailTemplateId]);

  const [emailGuests, { isLoading: isSending }] = useEmailGuestsMutation();
  const [currentSendIndex, setCurrentSendIndex] = useState<number | null>(null);

  const handleAddAutomation = () => {
    const newReminder: EmailReminder = {
      timeUnits: 1,
      timeUnitOfMeasure: "d",
      isActive: true,
      recipientFilterType: "all",
    };
    const newReminders = [...(data?.emailReminders ?? []), newReminder];
    dispatch(
      updateField({
        field: "emailReminders",
        value: newReminders,
      }),
    );
    props.onChange(data?.emailRemindersRelativeTo!, newReminders);
  };

  const handleSendEmail = async (autoSendIndex: number) => {
    try {
      const request: EmailGuestsRequest = {
        emailTemplateId: props.emailTemplateId,
        isAutoSend: true,
        autoSendIndex,
      };
      await emailGuests(request).unwrap();
      displayInfoMessage("Emails sent successfully...");
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (!data) {
    return <Loader color="blue" />;
  }

  let rows: JSX.Element[] = [];
  rows = (data.emailReminders ?? []).map(
    (emailReminder: EmailReminder, index: number) => {
      return (
        <Group key={index} align="center" mt="md">
          <EbActionIcon
            aria-label="Delete automation"
            onClick={() => {
              const newReminders = [
                ...data.emailReminders.slice(0, index),
                ...data.emailReminders.slice(index + 1),
              ];
              dispatch(
                updateField({
                  field: "emailReminders",
                  value: newReminders,
                }),
              );
              props.onChange(data?.emailRemindersRelativeTo!, newReminders);
            }}
          >
            <EbTooltip label="Delete automation">
              <IconDelete />
            </EbTooltip>
          </EbActionIcon>
          <Switch
            label={emailReminder.isActive ? "Enabled" : "Disabled"}
            checked={emailReminder.isActive}
            onChange={(event) => {
              const newReminder = {
                ...emailReminder,
                isActive: event.currentTarget.checked,
              };
              const newReminders = [...data.emailReminders];
              newReminders[index] = newReminder;
              dispatch(
                updateField({
                  field: "emailReminders",
                  value: newReminders,
                }),
              );
              props.onChange(data?.emailRemindersRelativeTo!, newReminders);
            }}
          />
          <Text>Send email</Text>
          <NumberInput
            style={{ width: "75px" }}
            min={1}
            max={100}
            maxLength={3}
            allowNegative={false}
            allowDecimal={false}
            value={emailReminder.timeUnits}
            onChange={(value) => {
              let v = 0;
              if (typeof value === "number") {
                v = value;
              }
              const newReminder = { ...emailReminder, timeUnits: v };
              const newReminders = [...data.emailReminders];
              newReminders[index] = newReminder;
              dispatch(
                updateField({
                  field: "emailReminders",
                  value: newReminders,
                }),
              );
              props.onChange(data?.emailRemindersRelativeTo!, newReminders);
            }}
          />
          <DayWeekControl
            value={emailReminder.timeUnitOfMeasure}
            onChange={(dayWeek) => {
              const newReminder = {
                ...emailReminder,
                timeUnitOfMeasure: dayWeek as TimeUnitOfMeasure,
              };
              const newReminders = [...data.emailReminders];
              newReminders[index] = newReminder;
              dispatch(
                updateField({
                  field: "emailReminders",
                  value: newReminders,
                }),
              );
              props.onChange(data?.emailRemindersRelativeTo!, newReminders);
            }}
          ></DayWeekControl>
          <Text>
            before{" "}
            {data.emailRemindersRelativeTo === "ventStart"
              ? "event start time to"
              : "RSVP end time to"}
          </Text>
          <Select
            data={["All Guests", "Accepted Guests", "Undecided Guests"]}
            value={recipientFilterTypeToText(
              data.emailReminders[index].recipientFilterType,
            )}
            onChange={(filterType) => {
              const newReminder = {
                ...emailReminder,
                recipientFilterType: textToRecipientFilterType(filterType),
              };
              const newReminders = [...data.emailReminders];
              newReminders[index] = newReminder;
              dispatch(
                updateField({
                  field: "emailReminders",
                  value: newReminders,
                }),
              );
              props.onChange(data?.emailRemindersRelativeTo!, newReminders);
            }}
          />
          <Button
            disabled={hasChanges || isSending}
            rightSection={
              isSending && currentSendIndex === index ? (
                <Loader size={14} />
              ) : null
            }
            onClick={() => {
              setCurrentSendIndex(index);
              handleSendEmail(index);
            }}
          >
            Send Now
          </Button>
        </Group>
      );
    },
  );

  return (
    <>
      <Paper>
        <RelativeToControl
          label="Send emails automatically relative to"
          onChange={(relativeTo) => {
            dispatch(
              updateField({
                field: "emailRemindersRelativeTo",
                value: relativeTo,
              }),
            );
            props.onChange(relativeTo as RelativeTo, data?.emailReminders!);
          }}
          value={data?.emailRemindersRelativeTo}
        ></RelativeToControl>
        {rows}
        <Flex justify="flex-start">
          <Button mt="md" onClick={handleAddAutomation}>
            Add New Automation
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default EmailTemplateScheduler;
